// Light or dark, the same way Relatude.DB's admin UI does it: the user's saved choice wins, the
// system preference is the default, and the choice is kept in localStorage under "theme".
// index.html applies the same rule inline before the first paint; this module takes over from
// there. One value for the whole app, so every toggle shows the same state.
import { useCallback, useEffect, useState } from 'react'

export type Theme = 'light' | 'dark'

const storageKey = 'theme'
const systemDark = window.matchMedia?.('(prefers-color-scheme: dark)')

function savedTheme(): Theme | null {
  try {
    const saved = localStorage.getItem(storageKey)
    return saved === 'light' || saved === 'dark' ? saved : null
  } catch {
    return null
  }
}

export function getInitialTheme(): Theme {
  return savedTheme() ?? (systemDark?.matches ? 'dark' : 'light')
}

let current: Theme = getInitialTheme()
const listeners = new Set<() => void>()

function apply(theme: Theme) {
  current = theme
  document.documentElement.dataset.theme = theme
  for (const listener of listeners) listener()
}

/** Sets the theme and remembers it, which is what the toggle does. */
export function setTheme(theme: Theme): void {
  apply(theme)
  try {
    localStorage.setItem(storageKey, theme)
  } catch {
    // storage unavailable: the theme holds for this page only
  }
}

// Until the user has chosen, follow the system when it changes.
systemDark?.addEventListener?.('change', (e) => {
  if (savedTheme() === null) apply(e.matches ? 'dark' : 'light')
})

// In case the inline script in index.html did not run.
document.documentElement.dataset.theme = current

/** The current theme and a way to flip it. */
export function useTheme(): [Theme, () => void] {
  const [theme, setLocal] = useState<Theme>(current)
  useEffect(() => {
    const update = () => setLocal(current)
    listeners.add(update)
    update()
    return () => {
      listeners.delete(update)
    }
  }, [])
  const toggle = useCallback(() => setTheme(current === 'dark' ? 'light' : 'dark'), [])
  return [theme, toggle]
}
